// EPISTEME — Immutable audit state machine + snapshot + patch helpers

import type {
  Audit, AuditPatch, CommittedDecision, CredibilityScores, Domain,
  EvidenceItem, ProcessCheck, RevisionPlan, Snapshot, SufficiencyCriteria,
  Support, TrustGraph, UncertaintyState, CausalModel
} from './types';
import { updateCredence } from './epistemic';

export function createId() {
  return (crypto.randomUUID ? crypto.randomUUID() : 'a' + Math.random().toString(36).slice(2));
}

export function now() { return Date.now(); }

export function createEmptyUncertainty(): UncertaintyState {
  return {
    credence: 48,
    assumptions: [],
    alternatives: [],
    knownUnknowns: [],
    elicited: { low: 25, mid: 48, high: 72 },
  };
}

export function createEmptyTrust(): TrustGraph {
  return { nodes: [], edges: [] };
}

export function createDefaultSufficiency(): SufficiencyCriteria {
  return {
    minSources: 2,
    minCredence: 72,
    maxDisconfirmer: 1,
    textual: 'I will treat the claim as actionable only when I have at least two independent pieces of evidence with average credibility ≥ 3.2, my credence is above 70, and there is no strong credible disconfirming evidence.',
    exceptions: [],
  };
}

export function createDefaultRevision(): RevisionPlan {
  return {
    falsifiers: [],
    monitors: [],
    stopLoss: [],
  };
}

export function createAudit(domain: Domain, scenario: string, claim: string, sensitive = false): Audit {
  const id = createId();
  const t = now();
  return {
    id,
    created: t,
    updated: t,
    domain,
    scenario,
    claim,
    evidence: [],
    uncertainty: createEmptyUncertainty(),
    trust: createEmptyTrust(),
    sufficiency: createDefaultSufficiency(),
    revision: createDefaultRevision(),
    snapshots: [],
    outcomes: [],
    processChecks: [],
    sensitive,
    preMortems: [],
    preParades: [],
    causal: { nodes: [], edges: [] },
    personalBets: [],
  };
}

export function patchAudit(audit: Audit, patch: AuditPatch): Audit {
  const next: Audit = {
    ...audit,
    ...patch,
    updated: now(),
  };
  // auto-recompute live credence when evidence changes
  if (patch.evidence) {
    next.uncertainty = {
      ...next.uncertainty,
      credence: updateCredence(audit.uncertainty.credence, next.evidence),
    };
  }
  return next;
}

export function addEvidence(audit: Audit, item: Omit<EvidenceItem, 'id' | 'addedAt'>): Audit {
  const full: EvidenceItem = {
    ...item,
    id: createId(),
    addedAt: now(),
  };
  return patchAudit(audit, { evidence: [...audit.evidence, full] });
}

export function updateEvidence(audit: Audit, id: string, changes: Partial<EvidenceItem>): Audit {
  const evidence = audit.evidence.map(e => e.id === id ? { ...e, ...changes } : e);
  return patchAudit(audit, { evidence });
}

export function removeEvidence(audit: Audit, id: string): Audit {
  return patchAudit(audit, { evidence: audit.evidence.filter(e => e.id !== id) });
}

export function setCredence(audit: Audit, credence: number): Audit {
  return patchAudit(audit, {
    uncertainty: { ...audit.uncertainty, credence: Math.max(2, Math.min(98, Math.round(credence))) }
  });
}

export function addAssumption(audit: Audit, text: string): Audit {
  return patchAudit(audit, {
    uncertainty: { ...audit.uncertainty, assumptions: [...audit.uncertainty.assumptions, text] }
  });
}

export function addAlternative(audit: Audit, text: string): Audit {
  return patchAudit(audit, {
    uncertainty: { ...audit.uncertainty, alternatives: [...audit.uncertainty.alternatives, text] }
  });
}

export function setSufficiency(audit: Audit, suf: Partial<SufficiencyCriteria>): Audit {
  return patchAudit(audit, { sufficiency: { ...audit.sufficiency, ...suf } });
}

export function commitDecision(audit: Audit, action: string, rationale: string): Audit {
  const dec: CommittedDecision = {
    action,
    rationale,
    committedAt: now(),
    confidenceAtCommit: audit.uncertainty.credence,
  };
  return patchAudit(audit, { decision: dec });
}

export function setRevision(audit: Audit, rev: Partial<RevisionPlan>): Audit {
  return patchAudit(audit, { revision: { ...audit.revision, ...rev } });
}

export function addProcessCheck(audit: Audit, check: ProcessCheck): Audit {
  return patchAudit(audit, { processChecks: [...audit.processChecks, check] });
}

export function takeSnapshot(audit: Audit, label?: string): Audit {
  const snap: Snapshot = {
    id: createId(),
    takenAt: now(),
    label,
    audit: JSON.parse(JSON.stringify({ ...audit, snapshots: [], outcomes: [] })), // deep clone without cycles
  };
  return patchAudit(audit, { snapshots: [...audit.snapshots, snap] });
}

export function addOutcome(audit: Audit, outcome: string, result: 'confirmed' | 'disconfirmed' | 'mixed' | 'pending', lessons?: string): Audit {
  const log = {
    id: createId(),
    loggedAt: now(),
    outcome,
    result,
    updatedCredence: audit.uncertainty.credence,
    lessons,
  };
  return patchAudit(audit, { outcomes: [...audit.outcomes, log] });
}

export function reorderEvidence(audit: Audit, fromIndex: number, toIndex: number): Audit {
  const ev = [...audit.evidence];
  const [moved] = ev.splice(fromIndex, 1);
  ev.splice(toIndex, 0, moved);
  return patchAudit(audit, { evidence: ev });
}

// Helper to produce a clean serializable copy for export
export function cloneAuditForExport(audit: Audit) {
  return JSON.parse(JSON.stringify(audit));
}
